import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import ModalComponent from 'components/Common/Modal';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import useCrmEmails from 'api/hooks/useCrmEmails';
import type { CrmEmailListItem } from 'api/hooks/useCrmEmails';
import useUploadFiles from 'api/hooks/useUploadFiles';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import { buildDocumentSharePageV2Url } from 'utils/buildShareDocumentV2Link';

const AddToEmailCampaignButton = ({
  setIsMoreActionsOpen,
  setIsOpenAddEmail,
}: {
  setIsOpenAddEmail: (value: boolean) => void;
  setIsMoreActionsOpen: (value: boolean) => void;
}) => {
  return (
    <>
      <button
        className='w-100 d-flex align-items-center text-left general-button list-action-button px-3 py-2'
        onClick={() => {
          setIsOpenAddEmail(true);
          setIsMoreActionsOpen(false);
        }}
      >
        <span className='political-updates-nav-icon icon-email paragraph-p4 d-inline-block-centered specific-top-margin-1' />{' '}
        <span>Add to email campaign</span>
      </button>
    </>
  );
};

interface ModalContentProps {
  file: UploadHubListItem;
  openState: { isOpen: boolean; setIsOpen: (value: boolean) => void };
}

const emailLabel = (email: CrmEmailListItem) => email.title || email.subject || 'Untitled';

const AddToEmailCampaingPopup = ({ openState, file }: ModalContentProps) => {
  const { isOpen, setIsOpen } = openState ?? {};
  const navigate = useNavigate();
  const prepopulatedAttachment = {
    url: buildDocumentSharePageV2Url(file.publicId),
    fileName: file.fileName,
    contentType: file.contentType,
  };

  const { logEvent } = useUploadFiles();
  const logAddedToEmailCampaign = (targetId?: number) =>
    logEvent({
      eventName: 'UploadHubFileAddedToEmailCampaign',
      uploadHubItemId: file.id,
      targetId,
      isNewTarget: targetId === undefined,
    });

  const onConfirmNew = () => {
    logAddedToEmailCampaign();
    navigate({ pathname: '/people/emails/create' }, { state: { prepopulatedAttachment } });
  };

  const onConfirmExisting = (email: CrmEmailListItem) => {
    logAddedToEmailCampaign(email.crmEmailId);
    navigate({ pathname: `/people/emails/${email.crmEmailId}` }, { state: { prepopulatedAttachment } });
  };
  const fileName = file?.fileName ?? '';
  const { getDraftEmails } = useCrmEmails();
  const [option, setOption] = useState<'new' | 'existing'>('new');
  const [items, setItems] = useState<CrmEmailListItem[]>([]);
  const [selected, setSelected] = useState<CrmEmailListItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchItems = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchItems.current = async (signal) => {
    try {
      const results = await getDraftEmails({ signal });
      if (results) {
        setItems(results);
        setSelected(results[0] ?? null);
      }
      setIsLoading(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    fetchItems.current?.(controller.signal);
    return () => controller.abort('Draft emails list cancelled.');
  }, [isOpen]);

  const confirm = () => {
    if (option === 'new') onConfirmNew();
    else if (selected) onConfirmExisting(selected);
    setIsOpen(false);
  };

  return (
    <>
      <ModalComponent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        overlayClassName='react-modal-overlay adjustment-of-position'
      >
        <h3 className='p-4 border-bottom title-h4-bold mb-0'>Add “{fileName}” to an email campaign</h3>
        <div className='p-4'>
          <div className='d-md-flex align-items-center gap-10 mb-4'>
            <button
              className={`w-100 mb-4 mb-md-0 text-left general-button col-md border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
                option === 'new' ? 'selected' : ''
              }`}
              onClick={() => setOption('new')}
            >
              <span className='select-item d-inline-block-centered mr-2' />A new email campaign
            </button>
            <button
              className={`w-100 text-left general-button col-md border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
                option === 'existing' ? 'selected' : ''
              }`}
              onClick={() => setOption('existing')}
            >
              <span className='select-item d-inline-block-centered mr-2' />
              An existing draft campaign
            </button>
          </div>

          {option === 'existing' && (
            <div className='mb-4'>
              <DropdownComponent
                mainItem={() => (
                  <span className='specific-y-padding-5 d-inline-block' style={{ maxHeight: '47px' }}>
                    {isLoading ? 'Loading…' : selected ? emailLabel(selected) : 'Select a draft email'}
                  </span>
                )}
              >
                <CustomScrollbar
                  className='simple-scrollbar'
                  autoHeight
                  autoHeightMin='100%'
                  autoHeightMax='226px'
                  maximalThumbYSize={100}
                >
                  {isLoading ? (
                    <div className='p-3 light-60-text font-italic'>Loading…</div>
                  ) : items.length === 0 ? (
                    <div className='p-3 light-60-text font-italic'>You have no draft emails</div>
                  ) : (
                    <ul className='policy-list px-0 py-1 mb-0'>
                      {items.map((email) => (
                        <li
                          key={`draft-email-${email.crmEmailId}`}
                          onClick={() => setSelected(email)}
                          className='flex-centered specific-y-padding-8 px-3 list-action-button pointer'
                        >
                          <span className='flex-grow-1'>{emailLabel(email)}</span>
                          {selected?.crmEmailId === email.crmEmailId && <span className='icon-tick paragraph-p4' />}
                        </li>
                      ))}
                    </ul>
                  )}
                </CustomScrollbar>
              </DropdownComponent>
            </div>
          )}

          <div className='pt-3 d-flex justify-content-end'>
            <button
              onClick={confirm}
              disabled={option === 'existing' && !selected}
              className='general-button rounded-pill action-button px-5 specific-y-padding-8'
            >
              {option === 'new' ? 'Create email campaign' : 'Add to email campaign'}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  );
};

export { AddToEmailCampaingPopup };
export default AddToEmailCampaignButton;
